import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Printer, DollarSign, Pencil } from 'lucide-react';
import { printLabInvoice } from '../../lib/labInvoicePrint';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillDetail() {
  const { billId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', mode: 'cash', remarks: '' });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [billSettings, setBillSettings] = useState({});
  const [logoDataUrl, setLogoDataUrl] = useState(null);

  const fetchBill = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/bills/${billId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setBill(await res.json());
    setLoading(false);
  }, [billId, token]);

  const fetchBillSettings = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/settings/bill-template`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setBillSettings(await res.json());
    const logoRes = await fetch(`${API}/api/lis/settings/bill-logo/default`, { headers: { Authorization: `Bearer ${token}` } });
    if (logoRes.ok) {
      const blob = await logoRes.blob();
      const reader = new FileReader();
      reader.onloadend = () => setLogoDataUrl(reader.result);
      reader.readAsDataURL(blob);
    }
  }, [token]);

  useEffect(() => { fetchBill(); fetchBillSettings(); }, [fetchBill, fetchBillSettings]);

  const addPayment = async () => {
    const res = await fetch(`${API}/api/lis/bills/${billId}/payment`, { method: 'POST', headers, body: JSON.stringify(payForm) });
    if (res.ok) { toast.success('Payment added'); setShowPayDialog(false); setPayForm({ amount: '', mode: 'cash', remarks: '' }); fetchBill(); }
    else toast.error('Failed');
  };

  const openEdit = () => {
    setEditForm({ patient_name: bill.patient_name, patient_mobile: bill.patient_mobile, referred_by: bill.referred_by, total: bill.total, discount: bill.discount, amount_received: bill.amount_received, payment_mode: bill.payment_mode, remarks: bill.remarks });
    setShowEditDialog(true);
  };

  const saveBillEdit = async () => {
    const res = await fetch(`${API}/api/lis/bills/${billId}`, { method: 'PUT', headers, body: JSON.stringify(editForm) });
    if (res.ok) { toast.success('Bill updated'); setShowEditDialog(false); fetchBill(); }
    else toast.error('Failed');
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500">Loading...</div>;
  if (!bill) return <div className="text-center py-8 text-slate-500">Bill not found</div>;

  return (
    <div data-testid="lis-bill-detail" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/vendor/lis/cases')}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h1 className="text-xl font-bold text-slate-800">Bill #{bill.reg_no}</h1>
        <Badge className={bill.status === 'no_due' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{bill.status === 'no_due' ? 'Paid' : 'Due'}</Badge>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={openEdit}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
          {bill.balance > 0 && <Button size="sm" onClick={() => setShowPayDialog(true)}><DollarSign className="w-3.5 h-3.5 mr-1" /> Add Payment</Button>}
          <Button variant="outline" size="sm" onClick={() => printLabInvoice(bill, billSettings, logoDataUrl)} data-testid="print-download-bill-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print / Download</Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Patient</CardTitle></CardHeader><CardContent>
          <p className="font-medium">{bill.patient_name}</p>
          <p className="text-sm text-slate-500">{bill.patient_mobile}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Details</CardTitle></CardHeader><CardContent className="text-sm space-y-1">
          <p>Date: {(bill.created_at || '').slice(0, 10)}</p>
          <p>Referred By: {bill.referred_by || '-'}</p>
          <p>Centre: {bill.collection_centre || '-'}</p>
          <p>Processing Lab: {bill.processing_lab || '-'}</p>
        </CardContent></Card>
      </div>

      <Card className="mt-4 border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Items</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm"><thead><tr className="border-b bg-slate-50"><th className="p-2 text-left">Test/Panel</th><th className="p-2 text-right">MRP</th><th className="p-2 text-right">B2B</th></tr></thead>
            <tbody>
              {(bill.items || []).map((item, i) => (
                <tr key={`row-${i}`} className="border-b border-slate-100"><td className="p-2">{item.item_name}</td><td className="p-2 text-right">Rs.{item.price}</td><td className="p-2 text-right text-blue-600">Rs.{item.b2b_price}</td></tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="mt-4 border-slate-200"><CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div><p className="text-xs text-slate-500">Total</p><p className="text-lg font-bold">Rs.{bill.total}</p></div>
          <div><p className="text-xs text-slate-500">B2B Cost</p><p className="text-lg font-bold text-blue-600">Rs.{bill.total_b2b}</p></div>
          <div><p className="text-xs text-slate-500">Discount</p><p className="text-lg font-bold text-amber-600">Rs.{bill.discount}</p></div>
          <div><p className="text-xs text-slate-500">Received</p><p className="text-lg font-bold text-emerald-600">Rs.{bill.amount_received}</p></div>
          <div><p className="text-xs text-slate-500">Balance</p><p className={`text-lg font-bold ${bill.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Rs.{bill.balance}</p></div>
        </div>
      </CardContent></Card>

      {(bill.payments || []).length > 0 && (
        <Card className="mt-4 border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Payment History</CardTitle></CardHeader><CardContent>
          <div className="space-y-2">
            {bill.payments.map((p, i) => (
              <div key={`el-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                <span>Rs.{p.amount} via {p.mode}</span>
                <span className="text-xs text-slate-500">{(p.date || '').slice(0, 10)} by {p.received_by}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}><DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Add Payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input type="number" placeholder="Amount" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} />
          <Select value={payForm.mode} onValueChange={v => setPayForm(f => ({ ...f, mode: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem></SelectContent></Select>
          <Input placeholder="Remarks" value={payForm.remarks} onChange={e => setPayForm(f => ({ ...f, remarks: e.target.value }))} />
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowPayDialog(false)}>Cancel</Button><Button onClick={addPayment}>Add Payment</Button></DialogFooter>
      </DialogContent></Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}><DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Bill</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Patient Name" value={editForm.patient_name || ''} onChange={e => setEditForm(f => ({ ...f, patient_name: e.target.value }))} />
          <Input placeholder="Mobile" value={editForm.patient_mobile || ''} onChange={e => setEditForm(f => ({ ...f, patient_mobile: e.target.value }))} />
          <Input placeholder="Referred By" value={editForm.referred_by || ''} onChange={e => setEditForm(f => ({ ...f, referred_by: e.target.value }))} />
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-xs text-slate-500">Total</label><Input type="number" value={editForm.total || ''} onChange={e => setEditForm(f => ({ ...f, total: e.target.value }))} /></div>
            <div><label className="text-xs text-slate-500">Discount</label><Input type="number" value={editForm.discount || ''} onChange={e => setEditForm(f => ({ ...f, discount: e.target.value }))} /></div>
            <div><label className="text-xs text-slate-500">Received</label><Input type="number" value={editForm.amount_received || ''} onChange={e => setEditForm(f => ({ ...f, amount_received: e.target.value }))} /></div>
          </div>
          <Input placeholder="Remarks" value={editForm.remarks || ''} onChange={e => setEditForm(f => ({ ...f, remarks: e.target.value }))} />
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button><Button onClick={saveBillEdit}>Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
